import { IInventoryFilter, IInventoryFilterRequest } from "./models/products.models";

export interface OrderFilter {
  catalog_name: string | undefined;
  name: string | undefined;
  buyer_po: string | undefined;
  status: string | undefined;
  order_date_from: string | undefined;
  order_date_to: string | undefined;
  amount_total_min: number | undefined;
  amount_total_max: number | undefined;
}

export interface OrderFilterRequest {
  filters: { key: string; operator: string; value: any }[];
}

export const removeEmptyProperties = (obj: any, removeBoolFalse = true): any => {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map((item) => removeEmptyProperties(item, removeBoolFalse))
      .filter((item) => item !== undefined);
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  } else if (obj && typeof obj === 'object') {
    const newObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = removeEmptyProperties(obj[key], removeBoolFalse);
        const shouldKeep = value !== undefined && (removeBoolFalse ? value !== false : true);

        if (shouldKeep) {
          newObj[key] = value;
        }
      }
    }
    return Object.keys(newObj).length > 0 ? newObj : undefined;
  } else if (removeBoolFalse === true) {
    return obj === false ? undefined : obj;
  } else {
    return obj;
  }
};

export const deepConvertBooleans = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepConvertBooleans(item));
  } else if (typeof obj === 'object' && obj !== null) {
    const result: any = {};
    for (const key in obj) {
      result[key] = deepConvertBooleans(obj[key]);
    }
    return result;
  } else if (typeof obj === 'boolean') {
    return obj ? 1 : 0;
  } else {
    return obj;
  }
};
export const encodeFilterObject = (filterObject: any, removeBoolFalse = true): string | null => {
  if (filterObject) {
    const filter = deepConvertBooleans(removeEmptyProperties(filterObject, removeBoolFalse));
    if (filter) {
      const encodedFilter = encodeURIComponent(JSON.stringify(filter));
      return encodedFilter;
    }
  }
  return null;
};

export const refillOrderFilter = (filter?: OrderFilter): OrderFilter => {
  const refillFilter: OrderFilter = {
    catalog_name: filter?.catalog_name,
    name: filter?.name,
    buyer_po: filter?.buyer_po,
    status: filter?.status,
    order_date_from: filter?.order_date_from,
    order_date_to: filter?.order_date_to,
    amount_total_min: filter?.amount_total_min,
    amount_total_max: filter?.amount_total_max,
  };

  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseOrderFilterRequest = (filter: OrderFilter): OrderFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  if (filter?.catalog_name)
    filters.push({
      key: 'catalog_name',
      operator: 'contains',
      value: filter?.catalog_name,
    });
  if (filter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: filter?.name,
    });
  if (filter?.buyer_po)
    filters.push({
      key: 'buyer_po',
      operator: 'contains',
      value: filter?.buyer_po,
    });
  if (filter?.status)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: filter?.status,
    });
  if (filter?.order_date_from)
    filters.push({
      key: 'date_order',
      operator: 'greater_than_or_equals',
      value: filter?.order_date_from,
    });
  if (filter?.order_date_to)
    filters.push({
      key: 'date_order',
      operator: 'less_than_or_equals',
      value: filter?.order_date_to,
    });
  if (filter?.amount_total_min)
    filters.push({
      key: 'amount_total',
      operator: 'greater_than_or_equals',
      value: filter?.amount_total_min,
    });
  if (filter?.amount_total_max)
    filters.push({
      key: 'amount_total',
      operator: 'less_than_or_equals',
      value: filter?.amount_total_max,
    });

  return { filters };
};

export function parseFilterRequest(
  module: string | null,
  filter: OrderFilter
): OrderFilterRequest | undefined {
  switch (module) {
    case 'order':
      return parseOrderFilterRequest(filter as OrderFilter);
    default:
      return undefined;
  }
}

export const isValidDate = (dateString: string | undefined): boolean => {
  if(dateString == undefined)
    return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}


export const refillInventoryFilter = (
  filter?: IInventoryFilter
): IInventoryFilter => {
  const refillFilter: IInventoryFilter = {
    productsInfoFilter: {
      name: filter?.productsInfoFilter?.name,
      sku: filter?.productsInfoFilter?.sku,
      barcode: filter?.productsInfoFilter?.barcode,
      brands: filter?.productsInfoFilter?.brands || [],
    },
    inventoryFilter: {
      sales_price_min: filter?.inventoryFilter?.sales_price_min,
      sales_price_max: filter?.inventoryFilter?.sales_price_max,
      quantity_min: filter?.inventoryFilter?.quantity_min,
      quantity_max: filter?.inventoryFilter?.quantity_max,
      reorder_point_max: filter?.inventoryFilter?.reorder_point_max,
      expiry_date_min: isValidDate(filter?.inventoryFilter?.expiry_date_min) ? filter?.inventoryFilter?.expiry_date_min : undefined,
      expiry_date_max: isValidDate(filter?.inventoryFilter?.expiry_date_max) ? filter?.inventoryFilter?.expiry_date_max : undefined,
    },
    organizeFilter: {
      categories: filter?.organizeFilter?.categories || [],
      tags: filter?.organizeFilter?.tags || [],
    },
  };

  return JSON.parse(JSON.stringify(refillFilter));
};


export const parseInventoryFilterRequest = (
  filter: IInventoryFilter
): IInventoryFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const productsInfoFilter = filter?.productsInfoFilter;
  if (productsInfoFilter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: productsInfoFilter?.name,
    });
  if (productsInfoFilter?.sku)
    filters.push({
      key: 'sku',
      operator: 'contains',
      value: productsInfoFilter?.sku,
    });
    if (productsInfoFilter?.barcode != null)
    filters.push({
      key: 'barcode',
      operator: 'contains',
      value: productsInfoFilter?.barcode,
    })
  if (productsInfoFilter?.brands?.length > 0)
    filters.push({
      key: 'brand',
      operator: 'in',
      value: productsInfoFilter?.brands.map((brand) => brand.id),
    });

  const inventoryFilter = filter?.inventoryFilter;
  
  if (inventoryFilter?.sales_price_min !== undefined)
    filters.push({
      key: 'sales_price',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.sales_price_min,
    });
  if (inventoryFilter?.sales_price_max !== undefined)
    filters.push({
      key: 'sales_price',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.sales_price_max,
    });
  if (inventoryFilter?.quantity_min !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.quantity_min,
    });
  if (inventoryFilter?.quantity_max !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.quantity_max,
    });
  if (inventoryFilter?.reorder_point_max !== undefined)
    filters.push({
      key: 'reorder_point',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.reorder_point_max,
    });
  if (inventoryFilter?.expiry_date_min !== undefined && isValidDate(inventoryFilter?.expiry_date_min))
    filters.push({
      key: 'expiry_date',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.expiry_date_min,
    });
  if (inventoryFilter?.expiry_date_max !== undefined && isValidDate(inventoryFilter?.expiry_date_max))
    filters.push({
      key: 'expiry_date',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.expiry_date_max,
    });

  const organizeFilter = filter?.organizeFilter;
  if (organizeFilter?.categories?.length > 0)
    filters.push({
      key: 'category',
      operator: 'in',
      value: organizeFilter?.categories.map((category) => category.id),
    });
  if (organizeFilter?.tags?.length > 0)
    filters.push({
      key: 'tag',
      operator: 'in',
      value: organizeFilter?.tags.map((tag) => tag.id),
    });

  return { filters };
};
